import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { config } from '../config';

interface User {
  id: string;
  email: string;
  full_name: string | null;
  created_at: string;
  updated_at: string;
}

interface ImpersonationState {
  isImpersonating: boolean;
  targetUserId: string | null;
  targetUserEmail: string | null;
  originalToken: string | null;
}

export const AdminPanel: React.FC = () => {
  const { session } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [impersonation, setImpersonation] = useState<ImpersonationState>({
    isImpersonating: false,
    targetUserId: null,
    targetUserEmail: null,
    originalToken: null,
  });

  // Check if user is admin
  const adminEmails = ['neeliyer14@gmail.com', 'alanlu1999@gmail.com', 'richard@taiki.online'];
  const isAdmin = session?.user?.email && adminEmails.includes(session.user.email);

  const fetchUsers = async () => {
    if (!session?.access_token) {
      setError('No session token available. Please log in.');
      return;
    }
    
    console.log('🔑 Fetching users with session:', { 
      email: session?.user?.email, 
      hasToken: !!session?.access_token,
      tokenPreview: session.access_token.substring(0, 20) + '...'
    });
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(config.getApiUrl('/api/admin/users'), {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Error response:', errorText);
        throw new Error(`Failed to fetch users: ${response.statusText} - ${errorText}`);
      }

      const data = await response.json();
      setUsers(data.users || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch users');
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  const startImpersonation = (user: User) => {
    if (!session?.access_token) return;
    setError(null);

    try {
      console.log('🎭 Starting impersonation of user:', user.email, user.id);
      
      // Store impersonation state (just user ID and metadata)
      const newState: ImpersonationState = {
        isImpersonating: true,
        targetUserId: user.id,
        targetUserEmail: user.email,
        originalToken: null, // Not needed anymore
      };
      
      // Save impersonation state
      localStorage.setItem('admin_impersonation_state', JSON.stringify(newState));
      
      // Store the user ID for the fetch interceptor
      localStorage.setItem('admin_impersonate_user_id', user.id);
      
      console.log('✅ Impersonation state saved. Admin stays logged in as admin.');
      console.log('   Target user ID:', user.id);
      console.log('   Target user email:', user.email);
      
      setImpersonation(newState);
      
      // Redirect to home page
      console.log('🔄 Redirecting to home page...');
      window.location.href = '/';
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start impersonation');
      console.error('Error starting impersonation:', err);
    }
  };

  if (!isAdmin) {
    return null; // Don't show anything if not admin
  }

  return (
    <div style={{
        maxWidth: '1200px',
        margin: '20px auto',
        padding: '20px',
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      }}>
        <div style={{ marginBottom: '20px' }}>
          <h2 style={{ margin: '0 0 8px 0', color: '#000' }}>Admin Panel</h2>
          <p style={{ margin: 0, color: '#000', fontSize: '14px' }}>
            Manage and impersonate users
          </p>
        </div>

        <button
          onClick={fetchUsers}
          disabled={loading}
          style={{
            backgroundColor: '#4CAF50',
            color: '#000',
            border: 'none',
            padding: '10px 20px',
            borderRadius: '6px',
            cursor: loading ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            marginBottom: '20px',
          }}
        >
          {loading ? 'Loading...' : 'Load Users'}
        </button>

        {error && (
          <div style={{
            backgroundColor: '#fee',
            border: '1px solid #fcc',
            color: '#000',
            padding: '12px',
            borderRadius: '6px',
            marginBottom: '20px',
            fontWeight: '500',
          }}>
            {error}
          </div>
        )}

        {users.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '14px',
            }}>
              <thead>
                <tr style={{ backgroundColor: '#f5f5f5' }}>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd', color: '#000' }}>Email</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd', color: '#000' }}>Name</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd', color: '#000' }}>User ID</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd', color: '#000' }}>Created</th>
                  <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #ddd', color: '#000' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '12px', color: '#000' }}>{user.email}</td>
                    <td style={{ padding: '12px', color: '#000' }}>{user.full_name || '-'}</td>
                    <td style={{ padding: '12px', fontFamily: 'monospace', fontSize: '12px', color: '#000' }}>
                      {user.id.substring(0, 8)}...
                    </td>
                    <td style={{ padding: '12px', fontSize: '12px', color: '#000' }}>
                      {new Date(user.created_at).toLocaleDateString()}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      <button
                        onClick={() => startImpersonation(user)}
                        disabled={impersonation.isImpersonating}
                        style={{
                          backgroundColor: impersonation.isImpersonating ? '#ccc' : '#2196F3',
                          color: '#000',
                          border: 'none',
                          padding: '6px 12px',
                          borderRadius: '4px',
                          cursor: impersonation.isImpersonating ? 'not-allowed' : 'pointer',
                          fontSize: '12px',
                          fontWeight: '600',
                        }}
                      >
                        Impersonate
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {users.length === 0 && !loading && (
          <p style={{ textAlign: 'center', color: '#000', padding: '40px 0' }}>
            Click "Load Users" to see the list
          </p>
        )}
      </div>
  );
};

